import type Stripe from 'stripe-latest';
import type { StripePriceLimitType } from '../model-typings/stripepricelimits';
/* eslint-disable @typescript-eslint/naming-convention */
export type AccessLevels = 'free' | 'premium';
export type PaymentMethodResponse = {
  sessionUrl?: string | null,
  paymentMethod?: Stripe.PaymentMethod | null,
};

/*
  Stripe price API ids in the past could be specified by the stripe user. They removed this functionality.
  So now when a new stripe price is made, the API ID is price_<random string>. When we
  made free-seat, premium-seat, and premium-seat-2, we could specify the API ID and make
  corresponding test and live mode IDs. Unfortunately, we can no longer have the same API ID
  in live and test mode. Luckily, stripe’s public and private keys include ‘live’ and ‘test’ in
  the key itself to visually distinguish between the two modes. We can use this to change the value
  of our hard coded constants to reflect the API ID based on environment as specified by the stripe keys.

  The problem with using the stripe keys to determine which stripe plan API IDs to use is that we cannot bundle
  the stripe public and private keys in the FE app. So we need to find a way around this. That means that
  modules/fixutres/featureFlags.ts cannot import config because the FE bundles the file and sends to the browser.

  So we're making fixed constants for the API IDs that appear on the front end, and will map them later on the BE
  in the SubscriptionManager. The SubscriptionManager will then handle setting the API ID based on the environment.
*/
export const PREMIUM_PER_REVISION_PLAN_NAME = 'premium-per-revision';
export const PREMIUM_COMPLIANCE_PLAN_NAME = 'premium-compliance-only-1';
export const PREMIUM_COMP_SEC_PLAN_NAME = 'premium-compliance-and-security-1';
export const FREE_PLAN_NAME = 'free-seat';
export const LEGACY_PREMIUM_PLAN_1 = 'premium-seat';
export const LEGACY_PREMIUM_PLAN_2 = 'premium-seat-2';
export const ENTERPRISE_COMPLIANCE = 'enterprise-compliance';
export const ENTERPRISE_PLATFORM = 'enterprise-platform';
export const ENTERPRISE_RELEASE_MANAGEMENT = 'enterprise-release-management';

export const PROD_COMPLIANCE_ONLY_STRIPE_PLAN = 'price_1KWU0FK3tvbcFWvu8Er0Usb4';
export const PROD_COMPLIANCE_SECURITY_STRIPE_PLAN = 'price_1N0YRsK3tvbcFWvu4pfMuRS7';
export const TEST_COMPLIANCE_ONLY_STRIPE_PLAN = 'price_1KnYzuK3tvbcFWvuZ9ejEKrz';
export const TEST_COMPLIANCE_SECURITY_STRIPE_PLAN = 'price_1KnZCUK3tvbcFWvu3esOlbC3';

export const fossaPlanNames = [
  FREE_PLAN_NAME,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
  PREMIUM_COMPLIANCE_PLAN_NAME,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_PER_REVISION_PLAN_NAME,
  ENTERPRISE_COMPLIANCE,
  ENTERPRISE_PLATFORM,
  ENTERPRISE_RELEASE_MANAGEMENT,
] as const;

export const STRIPE_PRICE_PREFIX = 'price_';
export type DynamicPlanName = `${typeof STRIPE_PRICE_PREFIX}${string}`;
export type StaticPlanName = typeof fossaPlanNames[number];
export type FossaPlanName = StaticPlanName | DynamicPlanName;

// Not a huge fan of this, but we don't map Enterprise in our plan names anywhere. I'd rather handle it here only
// than change featureFlags content to include it.
export type FossaPlanNameWithEnterprise = FossaPlanName | 'Enterprise';

// There's a chance that the org has the API price name in the db. Map the API name to the plaintext name from above.
// TODO: remove this once every org has the plaintext name subscription name
export const apiPriceToPlanNameMap: Record<string, FossaPlanName> = {
  [PROD_COMPLIANCE_ONLY_STRIPE_PLAN]: PREMIUM_COMPLIANCE_PLAN_NAME,
  [PROD_COMPLIANCE_SECURITY_STRIPE_PLAN]: PREMIUM_COMP_SEC_PLAN_NAME,
  [TEST_COMPLIANCE_ONLY_STRIPE_PLAN]: PREMIUM_COMPLIANCE_PLAN_NAME,
  [TEST_COMPLIANCE_SECURITY_STRIPE_PLAN]: PREMIUM_COMP_SEC_PLAN_NAME,
} as const;

/**
 * Mostly used for testing purposes, but an array of all the enterprise plan names.
 */
export const enterprisePlans: FossaPlanName[] = [
  ENTERPRISE_COMPLIANCE,
  ENTERPRISE_PLATFORM,
  ENTERPRISE_RELEASE_MANAGEMENT,
];

export type FossaSubscriptionDetails = {
  label: string;
  price: number;
  description: string;
  privateRepos: number | 'Unlimited';
};

export type StripePlanMapItem = FossaSubscriptionDetails & {
  access_level: AccessLevels;
  price: number;
  planQuantity: (contributorCount: number) => number;
}

export const fossaPlanMap: Record<FossaPlanName, StripePlanMapItem> = {
  [FREE_PLAN_NAME]: {
    access_level: 'free',
    price: 0,
    planQuantity(contributorCount: number): number {
      return contributorCount;
    },
    label: 'Free',
    description: 'Compliance',
    privateRepos: 5,
  },
  [LEGACY_PREMIUM_PLAN_1]: {
    access_level: 'premium',
    price: 26,
    planQuantity(contributorCount: number): number {
      return Math.min(contributorCount, 150);
    },
    label: '(Legacy) Business',
    description: 'Compliance',
    privateRepos: 'Unlimited',
  },
  [LEGACY_PREMIUM_PLAN_2]: {
    access_level: 'premium',
    price: 46,
    planQuantity: (contributorCount: number): number => contributorCount,
    label: '(Legacy) Business',
    description: 'Compliance',
    privateRepos: 'Unlimited',
  },
  [PREMIUM_COMPLIANCE_PLAN_NAME]: {
    access_level: 'premium',
    price: 52,
    planQuantity: (contributorCount: number): number => contributorCount,
    label: 'Business',
    description: 'Compliance',
    privateRepos: 'Unlimited',
  },
  [PREMIUM_COMP_SEC_PLAN_NAME]: {
    access_level: 'premium',
    price: 76,
    planQuantity: (contributorCount: number): number => contributorCount,
    label: 'Business',
    description: 'Compliance + Security',
    privateRepos: 'Unlimited',
  },
  [PREMIUM_PER_REVISION_PLAN_NAME]: {
    access_level: 'premium',
    price: 0,
    planQuantity: (contributorCount: number): number => contributorCount,
    label: 'Business',
    description: 'Compliance + Security, per-revision pricing model',
    privateRepos: 'Unlimited',
  },
  [ENTERPRISE_COMPLIANCE]: {
    access_level: 'premium',
    price: 0,
    planQuantity: (contributorCount: number): number => contributorCount,
    label: 'Enterprise',
    description: 'SCA Compliance',
    privateRepos: 'Unlimited',
  },
  [ENTERPRISE_PLATFORM]: {
    access_level: 'premium',
    price: 0,
    planQuantity: (contributorCount: number): number => contributorCount,
    label: 'Enterprise',
    description: 'SCA Platform',
    privateRepos: 'Unlimited',
  },
  [ENTERPRISE_RELEASE_MANAGEMENT]: {
    access_level: 'premium',
    price: 0,
    planQuantity: (contributorCount: number): number => contributorCount,
    label: 'Enterprise',
    description: 'Release Management',
    privateRepos: 'Unlimited',
  },
} as const;

export type FossaSubscriptionWithPlanName = FossaSubscriptionDetails & { planName: FossaPlanName };

export const ACCESS_LEVEL_TO_PLAN: Record<string, FossaPlanNameWithEnterprise> = {
  free: FREE_PLAN_NAME,
  business: PREMIUM_COMPLIANCE_PLAN_NAME,
  enterprise: 'Enterprise',
};

export const ENTERPRISE_CONTRIBUTOR_LIMIT_DEFAULT = 100;
export const ENTERPRISE_RELEASE_GROUP_LIMIT_DEFAULT = 5;

export const MAXIMUM_BUSINESS_SEATS = 25;

/** Minimum number of contributors allowed. */
export const MINIMUM_BUSINESS_SEATS = 5;

/** Minimum number of contributors to add/remove from the count. */
export const MINIMUM_BUSINESS_SEATS_STEP = 5;

export type Plan = {
  type: StripePriceLimitType,
  prices: Stripe.Price[];
};
